/**
 * How Firestore talks to its backend on the web (#791).
 *
 * Firestore's web SDK holds one streaming request open and reads each answer
 * off it as it arrives. In Safari that sometimes stops working mid-session:
 * an answer is held until the request closes, and every read waits up to 30 s.
 * Over 60 days every recipe open slower than 25 s was on WebKit (Mac Safari,
 * iOS Safari, Chrome iOS); the founder's own desktop Chrome never stalled
 * (0 of 112 opens over 15 s, against 12 of 185 in Safari on the same account).
 * Most of the stalls went through iCloud Private Relay's Cloudflare exits,
 * which only Safari uses, and one came from a home connection.
 *
 * The SDK checks for a buffering proxy only when it connects, so a stream that
 * goes bad later is never caught. Long polling has no stream to go bad: every
 * answer completes its own request. It cost ~13 ms at the median in WebKit
 * (61 ms against 48 ms over 30 reads). So it is forced where the stalls are,
 * and left to the SDK everywhere else.
 *
 * `EXPO_PUBLIC_FIRESTORE_LONG_POLLING=force|auto` overrides the choice, so a
 * Chrome harness can run the app on long polling.
 */

export type FirestoreTransport = 'long-polling' | 'auto';

interface TransportInputs {
  userAgent?: string;
  /** `navigator.maxTouchPoints`: iPadOS Safari reports a Mac user agent. */
  maxTouchPoints?: number;
  /** `EXPO_PUBLIC_FIRESTORE_LONG_POLLING`. */
  override?: string;
}

/** Every iOS browser is WebKit underneath, Chrome and Firefox included. */
const IOS = /\b(iPhone|iPad|iPod)\b/;
/** Browsers that put "Safari" in their user agent but are not WebKit's Safari. */
const NOT_SAFARI = /(Chrome|Chromium|CriOS|FxiOS|EdgiOS|Edg\/|OPR\/|Firefox|Android)/;

export function isWebKitBrowser(userAgent: string, maxTouchPoints = 0): boolean {
  if (IOS.test(userAgent)) return true;
  // iPadOS asks for the desktop site by default and says it is a Mac.
  if (/Macintosh/.test(userAgent) && maxTouchPoints > 1) return true;
  return /AppleWebKit/.test(userAgent) && /Safari\//.test(userAgent) && !NOT_SAFARI.test(userAgent);
}

export function firestoreTransportFor({ userAgent = '', maxTouchPoints = 0, override }: TransportInputs): FirestoreTransport {
  if (override === 'force') return 'long-polling';
  if (override === 'auto') return 'auto';
  return isWebKitBrowser(userAgent, maxTouchPoints) ? 'long-polling' : 'auto';
}

/** The transport this page uses. Native apps never reach the web SDK's WebChannel. */
export function currentFirestoreTransport(isWeb: boolean): FirestoreTransport {
  if (!isWeb || typeof navigator === 'undefined') return 'auto';
  return firestoreTransportFor({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    override: process.env.EXPO_PUBLIC_FIRESTORE_LONG_POLLING,
  });
}
